// ============================================
// CRM Dashboard - Comprehensive View with Tabs
// ============================================

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  ShoppingCart,
  MessageSquare,
  BarChart3,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  ArrowLeft,
  Search,
  Filter,
  Phone,
  Package,
  TrendingUp,
  Calendar,
  Eye,
  ChevronDown,
  Building2,
  FileText,
  DollarSign,
  Activity
} from 'lucide-react';
import { useUIStore, useToast } from '../stores';
import { 
  leadsApi, 
  messagesApi, 
  orderPacksApi, 
  dashboardApi,
  type Lead, 
  type Conversation,
  type DashboardStats 
} from '../lib/api';
import { PageLoader, ProjectBadge, StageBadge, EmptyState, ErrorState } from '../components';
import { cn, formatRelativeTime, formatCurrency, formatDateTime, stageLabels, truncate } from '../lib/utils';
import { useTranslation } from '../i18n/useTranslation';

type TabType = 'overview' | 'leads' | 'orders' | 'messages' | 'analytics';

interface TabConfig {
  id: TabType;
  label: string;
  icon: React.ReactNode;
  count?: number;
}

export function CRMDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();
  const activeProject = useUIStore((state) => state.activeProject);
  
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  // Fetch dashboard stats
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['dashboard-stats', activeProject],
    queryFn: () => dashboardApi.stats(activeProject === 'all' ? undefined : activeProject),
    refetchInterval: 30000,
  });

  // Fetch all leads
  const { data: leadsData, isLoading: leadsLoading, refetch: refetchLeads } = useQuery({
    queryKey: ['all-leads', activeProject, stageFilter],
    queryFn: async () => {
      const projectId = activeProject === 'all' ? undefined : activeProject;
      const params: Record<string, string> = { limit: '100' };
      if (projectId) params.project_id = projectId;
      if (stageFilter !== 'all') params.stage = stageFilter;
      return leadsApi.list(params);
    },
    refetchInterval: 15000,
  });

  // Fetch conversations
  const { data: conversationsData, isLoading: conversationsLoading } = useQuery({
    queryKey: ['conversations', activeProject],
    queryFn: () => {
      const projectId = activeProject === 'all' ? undefined : activeProject;
      return messagesApi.listConversations(projectId ? { project_id: projectId, limit: '50' } : { limit: '50' });
    },
    refetchInterval: 15000,
  });

  const leads = leadsData?.leads || [];
  const conversations = conversationsData?.conversations || [];

  // Filter leads by search query
  const filteredLeads = leads.filter(lead => {
    if (!searchQuery) return true;
    const name = lead.contact ? `${lead.contact.name_first} ${lead.contact.name_last}`.toLowerCase() : '';
    const product = lead.product?.title?.toLowerCase() || '';
    return name.includes(searchQuery.toLowerCase()) || product.includes(searchQuery.toLowerCase());
  });

  // Calculate stats
  const leadsByStage = stats?.leadsByStage || {};
  const totalLeads = Object.values(leadsByStage).reduce((sum: number, count) => sum + (count as number), 0);
  const unreadMessages = conversations.reduce((sum, conv) => sum + conv.unread_count, 0);

  // Tab configurations
  const tabs: TabConfig[] = [
    { id: 'overview', label: 'نظرة عامة', icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'leads', label: 'العملاء', icon: <Users className="w-4 h-4" />, count: totalLeads },
    { id: 'orders', label: 'الطلبات', icon: <ShoppingCart className="w-4 h-4" />, count: leadsByStage.approved || 0 },
    { id: 'messages', label: 'الرسائل', icon: <MessageSquare className="w-4 h-4" />, count: unreadMessages },
    { id: 'analytics', label: 'التحليلات', icon: <TrendingUp className="w-4 h-4" /> },
  ];

  const handleRefresh = () => {
    refetchStats();
    refetchLeads();
    toast.info('جاري التحديث...');
  };

  if (statsLoading) return <PageLoader />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">لوحة تحكم CRM</h1>
          <p className="text-slate-400 mt-1">إدارة شاملة للعملاء والطلبات والرسائل</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleRefresh} className="btn-secondary">
            <RefreshCw className="w-4 h-4" />
            تحديث
          </button>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-1">
        <div className="flex flex-wrap gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all',
                activeTab === tab.id
                  ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/25'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              )}
            >
              {tab.icon}
              <span>{tab.label}</span>
              {tab.count !== undefined && tab.count > 0 && (
                <span className={cn(
                  'px-2 py-0.5 rounded-full text-xs font-bold',
                  activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-slate-700 text-slate-300'
                )}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[500px]">
        {activeTab === 'overview' && (
          <OverviewTab stats={stats} leads={leads} conversations={conversations} onLeadClick={(id) => navigate(`/leads/${id}`)} />
        )}
        {activeTab === 'leads' && (
          <LeadsTab 
            leads={filteredLeads} 
            loading={leadsLoading}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            stageFilter={stageFilter}
            setStageFilter={setStageFilter}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            onLeadClick={(id) => navigate(`/leads/${id}`)}
          />
        )}
        {activeTab === 'orders' && (
          <OrdersTab leads={leads.filter(l => ['approved', 'funded', 'closed_won'].includes(l.stage))} onLeadClick={(id) => navigate(`/leads/${id}`)} />
        )}
        {activeTab === 'messages' && (
          <MessagesTab conversations={conversations} loading={conversationsLoading} onConversationClick={(leadId) => navigate(`/whatsapp?lead=${leadId}`)} />
        )}
        {activeTab === 'analytics' && (
          <AnalyticsTab stats={stats} leads={leads} />
        )}
      </div>
    </div>
  );
}

// ============================================
// Overview Tab
// ============================================
interface OverviewTabProps {
  stats?: DashboardStats;
  leads: Lead[];
  conversations: Conversation[];
  onLeadClick: (id: string) => void;
}

function OverviewTab({ stats, leads, conversations, onLeadClick }: OverviewTabProps) {
  const { t } = useTranslation();
  
  const newLeads = leads.filter(l => l.stage === 'new').slice(0, 5);
  const waitingLeads = leads.filter(l => l.stage === 'waiting_eligibility').slice(0, 5);
  const approvedLeads = leads.filter(l => l.stage === 'approved').slice(0, 5);
  
  return (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <QuickStatCard 
          icon={<Users className="w-6 h-6" />} 
          label="إجمالي العملاء" 
          value={stats?.today?.newLeads || 0} 
          subtext="اليوم"
          color="blue" 
        />
        <QuickStatCard 
          icon={<Clock className="w-6 h-6" />} 
          label="بانتظار الأهلية" 
          value={stats?.leadsByStage?.waiting_eligibility || 0} 
          subtext="يحتاج متابعة"
          color="amber" 
          alert
        />
        <QuickStatCard 
          icon={<CheckCircle className="w-6 h-6" />} 
          label="معدل الموافقة" 
          value={`${Math.round((stats?.conversionRates?.eligibility_approval_rate || 0) * 100)}%`} 
          subtext="من طلبات الأهلية"
          color="green" 
        />
        <QuickStatCard 
          icon={<MessageSquare className="w-6 h-6" />} 
          label="رسائل غير مقروءة" 
          value={conversations.reduce((sum, c) => sum + c.unread_count, 0)} 
          subtext="تحتاج رد"
          color="purple" 
          alert={conversations.some(c => c.unread_count > 0)}
        />
      </div>

      {/* Three Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* New Leads */}
        <div className="card">
          <div className="p-4 border-b border-slate-700/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <h3 className="font-semibold text-white">عملاء جدد</h3>
              </div>
              <span className="px-2 py-1 rounded-full bg-blue-500/20 text-blue-400 text-xs font-bold">
                {newLeads.length}
              </span>
            </div>
          </div>
          <div className="p-2 max-h-80 overflow-y-auto">
            {newLeads.length === 0 ? (
              <EmptyState title="لا يوجد عملاء جدد" description="ستظهر العملاء الجدد هنا" />
            ) : (
              newLeads.map(lead => (
                <LeadListItem key={lead.id} lead={lead} onClick={() => onLeadClick(lead.id)} />
              ))
            )}
          </div>
        </div>

        {/* Waiting Eligibility */}
        <div className="card">
          <div className="p-4 border-b border-slate-700/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <Clock className="w-5 h-5 text-amber-400" />
                </div>
                <h3 className="font-semibold text-white">انتظار الأهلية</h3>
              </div>
              <span className="px-2 py-1 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold">
                {waitingLeads.length}
              </span>
            </div>
          </div>
          <div className="p-2 max-h-80 overflow-y-auto">
            {waitingLeads.length === 0 ? (
              <EmptyState title="لا يوجد عملاء" description="لا أحد ينتظر فحص الأهلية" />
            ) : (
              waitingLeads.map(lead => (
                <LeadListItem key={lead.id} lead={lead} onClick={() => onLeadClick(lead.id)} />
              ))
            )}
          </div>
        </div>

        {/* Approved - Not Entered */}
        <div className="card">
          <div className="p-4 border-b border-slate-700/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
                <h3 className="font-semibold text-white">موافق - لم يدخل HOT</h3>
              </div>
              <span className="px-2 py-1 rounded-full bg-green-500/20 text-green-400 text-xs font-bold">
                {approvedLeads.filter(l => !l.entered_in_hot).length}
              </span>
            </div>
          </div>
          <div className="p-2 max-h-80 overflow-y-auto">
            {approvedLeads.filter(l => !l.entered_in_hot).length === 0 ? (
              <EmptyState title="لا يوجد عملاء" description="جميع العملاء الموافقين تم إدخالهم" />
            ) : (
              approvedLeads.filter(l => !l.entered_in_hot).map(lead => (
                <LeadListItem key={lead.id} lead={lead} onClick={() => onLeadClick(lead.id)} showHotStatus />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card">
        <div className="p-4 border-b border-slate-700/50">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary-400" />
            <h3 className="font-semibold text-white">النشاط الأخير</h3>
          </div>
        </div>
        <div className="p-4">
          <div className="space-y-3">
            {leads.slice(0, 8).map(lead => (
              <div 
                key={lead.id}
                onClick={() => onLeadClick(lead.id)}
                className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                    <Users className="w-5 h-5 text-slate-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">
                      {lead.contact ? `${lead.contact.name_first} ${lead.contact.name_last}`.trim() || 'بدون اسم' : 'بدون اسم'}
                    </p>
                    <p className="text-sm text-slate-400">{lead.product?.title || 'بدون منتج'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <StageBadge stage={lead.stage} />
                  <span className="text-xs text-slate-500">{formatRelativeTime(lead.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Leads Tab
// ============================================
interface LeadsTabProps {
  leads: Lead[];
  loading: boolean;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  stageFilter: string;
  setStageFilter: (s: string) => void;
  showFilters: boolean;
  setShowFilters: (s: boolean) => void;
  onLeadClick: (id: string) => void;
}

function LeadsTab({ 
  leads, 
  loading, 
  searchQuery, 
  setSearchQuery, 
  stageFilter, 
  setStageFilter,
  showFilters,
  setShowFilters,
  onLeadClick 
}: LeadsTabProps) {
  const stages = [
    { value: 'all', label: 'الكل' },
    { value: 'new', label: 'جديد' },
    { value: 'contacted', label: 'تم التواصل' },
    { value: 'waiting_eligibility', label: 'انتظار الأهلية' },
    { value: 'approved', label: 'موافق' },
    { value: 'rejected', label: 'مرفوض' },
    { value: 'funded', label: 'ممول' },
    { value: 'closed_won', label: 'مغلق - نجاح' },
    { value: 'closed_lost', label: 'مغلق - خسارة' },
  ];

  return (
    <div className="space-y-4">
      {/* Search & Filters */}
      <div className="card p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="بحث عن عميل أو منتج..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pr-10 w-full"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              className="input min-w-[150px]"
            >
              {stages.map(stage => (
                <option key={stage.value} value={stage.value}>{stage.label}</option>
              ))}
            </select>
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className={cn('btn-secondary', showFilters && 'bg-primary-500/20 text-primary-400')}
            >
              <Filter className="w-4 h-4" />
              فلترة متقدمة
            </button>
          </div>
        </div>
      </div>

      {/* Leads Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700/50">
                <th className="text-right p-4 text-slate-400 font-medium">العميل</th>
                <th className="text-right p-4 text-slate-400 font-medium">المنتج</th>
                <th className="text-right p-4 text-slate-400 font-medium">المرحلة</th>
                <th className="text-right p-4 text-slate-400 font-medium">المصدر</th>
                <th className="text-right p-4 text-slate-400 font-medium">التاريخ</th>
                <th className="text-right p-4 text-slate-400 font-medium">حالة HOT</th>
                <th className="text-right p-4 text-slate-400 font-medium">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center">
                    <div className="spinner w-8 h-8 mx-auto" />
                  </td>
                </tr>
              ) : leads.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-slate-400">
                    لا يوجد عملاء مطابقين للبحث
                  </td>
                </tr>
              ) : (
                leads.map(lead => (
                  <tr 
                    key={lead.id}
                    className="border-b border-slate-700/30 hover:bg-slate-800/50 cursor-pointer transition-colors"
                    onClick={() => onLeadClick(lead.id)}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                          <Users className="w-5 h-5 text-slate-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">
                            {lead.contact ? `${lead.contact.name_first} ${lead.contact.name_last}`.trim() || 'بدون اسم' : 'بدون اسم'}
                          </p>
                          <p className="text-xs text-slate-500 font-mono">{lead.contact?.phone_e164_masked || '***'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <p className="text-slate-300">{truncate(lead.product?.title || 'بدون منتج', 25)}</p>
                      {lead.product?.price_cash && (
                        <p className="text-xs text-slate-500">{formatCurrency(lead.product.price_cash)}</p>
                      )}
                    </td>
                    <td className="p-4">
                      <StageBadge stage={lead.stage} />
                    </td>
                    <td className="p-4">
                      <span className="text-slate-400 text-sm">{lead.source}</span>
                    </td>
                    <td className="p-4">
                      <span className="text-slate-400 text-sm">{formatRelativeTime(lead.created_at)}</span>
                    </td>
                    <td className="p-4">
                      {lead.entered_in_hot ? (
                        <span className="flex items-center gap-1 text-green-400 text-sm">
                          <CheckCircle className="w-4 h-4" />
                          تم الإدخال
                        </span>
                      ) : lead.stage === 'approved' ? (
                        <span className="flex items-center gap-1 text-amber-400 text-sm">
                          <AlertTriangle className="w-4 h-4" />
                          لم يدخل
                        </span>
                      ) : (
                        <span className="text-slate-500 text-sm">-</span>
                      )}
                    </td>
                    <td className="p-4">
                      <button 
                        onClick={(e) => { e.stopPropagation(); onLeadClick(lead.id); }}
                        className="btn-icon hover:bg-primary-500/20 hover:text-primary-400"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {leads.length > 0 && (
          <div className="p-4 border-t border-slate-700/50">
            <p className="text-slate-400 text-sm">
              عرض {leads.length} عميل
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// Orders Tab
// ============================================
interface OrdersTabProps {
  leads: Lead[];
  onLeadClick: (id: string) => void;
}

function OrdersTab({ leads, onLeadClick }: OrdersTabProps) {
  const enteredOrders = leads.filter(l => l.entered_in_hot);
  const pendingOrders = leads.filter(l => !l.entered_in_hot);

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <QuickStatCard 
          icon={<ShoppingCart className="w-6 h-6" />} 
          label="إجمالي الطلبات" 
          value={leads.length} 
          color="blue" 
        />
        <QuickStatCard 
          icon={<CheckCircle className="w-6 h-6" />} 
          label="تم الإدخال في HOT" 
          value={enteredOrders.length} 
          color="green" 
        />
        <QuickStatCard 
          icon={<Clock className="w-6 h-6" />} 
          label="بانتظار الإدخال" 
          value={pendingOrders.length} 
          color="amber" 
          alert={pendingOrders.length > 0}
        />
        <QuickStatCard 
          icon={<DollarSign className="w-6 h-6" />} 
          label="قيمة الطلبات" 
          value={formatCurrency(leads.reduce((sum, l) => sum + (l.product?.price_cash || 0), 0))} 
          color="purple" 
        />
      </div>

      {/* Orders Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Orders */}
        <div className="card">
          <div className="p-4 border-b border-slate-700/50 bg-amber-500/5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                <h3 className="font-semibold text-white">طلبات بانتظار الإدخال في HOT</h3>
              </div>
              <span className="px-2 py-1 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold">
                {pendingOrders.length}
              </span>
            </div>
          </div>
          <div className="p-2 max-h-96 overflow-y-auto">
            {pendingOrders.length === 0 ? (
              <EmptyState title="لا توجد طلبات معلقة" description="جميع الطلبات تم إدخالها" />
            ) : (
              pendingOrders.map(lead => (
                <OrderCard key={lead.id} lead={lead} onClick={() => onLeadClick(lead.id)} />
              ))
            )}
          </div>
        </div>

        {/* Completed Orders */}
        <div className="card">
          <div className="p-4 border-b border-slate-700/50 bg-green-500/5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <h3 className="font-semibold text-white">طلبات تم إدخالها في HOT</h3>
              </div>
              <span className="px-2 py-1 rounded-full bg-green-500/20 text-green-400 text-xs font-bold">
                {enteredOrders.length}
              </span>
            </div>
          </div>
          <div className="p-2 max-h-96 overflow-y-auto">
            {enteredOrders.length === 0 ? (
              <EmptyState title="لا توجد طلبات مكتملة" description="ستظهر الطلبات المكتملة هنا" />
            ) : (
              enteredOrders.map(lead => (
                <OrderCard key={lead.id} lead={lead} onClick={() => onLeadClick(lead.id)} completed />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Messages Tab
// ============================================
interface MessagesTabProps {
  conversations: Conversation[];
  loading: boolean;
  onConversationClick: (leadId: string) => void;
}

function MessagesTab({ conversations, loading, onConversationClick }: MessagesTabProps) {
  const unreadConversations = conversations.filter(c => c.unread_count > 0);
  const readConversations = conversations.filter(c => c.unread_count === 0);

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <QuickStatCard 
          icon={<MessageSquare className="w-6 h-6" />} 
          label="إجمالي المحادثات" 
          value={conversations.length} 
          color="blue" 
        />
        <QuickStatCard 
          icon={<AlertTriangle className="w-6 h-6" />} 
          label="غير مقروءة" 
          value={unreadConversations.length} 
          color="amber" 
          alert={unreadConversations.length > 0}
        />
        <QuickStatCard 
          icon={<CheckCircle className="w-6 h-6" />} 
          label="تم الرد" 
          value={readConversations.length} 
          color="green" 
        />
      </div>

      {/* Conversations List */}
      <div className="card">
        <div className="p-4 border-b border-slate-700/50">
          <h3 className="font-semibold text-white">جميع المحادثات</h3>
        </div>
        <div className="divide-y divide-slate-700/30">
          {loading ? (
            <div className="p-8 text-center">
              <div className="spinner w-8 h-8 mx-auto" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="p-8">
              <EmptyState title="لا توجد محادثات" description="ستظهر المحادثات الجديدة هنا" />
            </div>
          ) : (
            conversations.map(conv => (
              <div
                key={conv.id}
                onClick={() => onConversationClick(conv.lead_id)}
                className={cn(
                  'flex items-center gap-4 p-4 cursor-pointer transition-colors',
                  conv.unread_count > 0 ? 'bg-primary-500/5 hover:bg-primary-500/10' : 'hover:bg-slate-800/50'
                )}
              >
                <div className={cn(
                  'w-12 h-12 rounded-full flex items-center justify-center',
                  conv.unread_count > 0 ? 'bg-primary-500/20' : 'bg-slate-700'
                )}>
                  <MessageSquare className={cn(
                    'w-6 h-6',
                    conv.unread_count > 0 ? 'text-primary-400' : 'text-slate-400'
                  )} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className={cn(
                      'font-medium truncate',
                      conv.unread_count > 0 ? 'text-white' : 'text-slate-300'
                    )}>
                      {conv.contact_name || 'بدون اسم'}
                    </p>
                    <span className="text-xs text-slate-500">
                      {formatRelativeTime(conv.last_message_at)}
                    </span>
                  </div>
                  <p className="text-sm text-slate-400 truncate mt-1">
                    {conv.summary || conv.contact_phone_masked}
                  </p>
                </div>
                {conv.unread_count > 0 && (
                  <span className="px-2.5 py-1 rounded-full bg-primary-500 text-white text-xs font-bold">
                    {conv.unread_count}
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// Analytics Tab
// ============================================
interface AnalyticsTabProps {
  stats?: DashboardStats;
  leads: Lead[];
}

function AnalyticsTab({ stats, leads }: AnalyticsTabProps) {
  const leadsByStage = stats?.leadsByStage || {};
  const stageData = [
    { stage: 'new', label: 'جديد', count: leadsByStage.new || 0, color: 'bg-blue-500' },
    { stage: 'waiting_eligibility', label: 'انتظار الأهلية', count: leadsByStage.waiting_eligibility || 0, color: 'bg-amber-500' },
    { stage: 'approved', label: 'موافق', count: leadsByStage.approved || 0, color: 'bg-green-500' },
    { stage: 'rejected', label: 'مرفوض', count: leadsByStage.rejected || 0, color: 'bg-red-500' },
    { stage: 'funded', label: 'ممول', count: leadsByStage.funded || 0, color: 'bg-purple-500' },
  ];

  const total = stageData.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="space-y-6">
      {/* Conversion Rates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card p-6">
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary-400" />
            معدلات التحويل
          </h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-400">معدل الموافقة على الأهلية</span>
                <span className="text-white font-bold">
                  {Math.round((stats?.conversionRates?.eligibility_approval_rate || 0) * 100)}%
                </span>
              </div>
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 rounded-full transition-all"
                  style={{ width: `${(stats?.conversionRates?.eligibility_approval_rate || 0) * 100}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-400">معدل التمويل</span>
                <span className="text-white font-bold">
                  {Math.round((stats?.conversionRates?.funding_rate || 0) * 100)}%
                </span>
              </div>
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-purple-500 rounded-full transition-all"
                  style={{ width: `${(stats?.conversionRates?.funding_rate || 0) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary-400" />
            توزيع المراحل
          </h3>
          <div className="space-y-3">
            {stageData.map(item => (
              <div key={item.stage}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-400">{item.label}</span>
                  <span className="text-white">{item.count}</span>
                </div>
                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className={cn('h-full rounded-full transition-all', item.color)}
                    style={{ width: total > 0 ? `${(item.count / total) * 100}%` : '0%' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-4 text-center">
          <p className="text-3xl font-bold text-white">{stats?.today?.newLeads || 0}</p>
          <p className="text-slate-400 text-sm mt-1">عملاء اليوم</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-3xl font-bold text-white">{stats?.today?.messages || 0}</p>
          <p className="text-slate-400 text-sm mt-1">رسائل اليوم</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-3xl font-bold text-green-400">{leadsByStage.approved || 0}</p>
          <p className="text-slate-400 text-sm mt-1">موافقات</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-3xl font-bold text-amber-400">{stats?.pendingAlerts || 0}</p>
          <p className="text-slate-400 text-sm mt-1">تنبيهات معلقة</p>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Helper Components
// ============================================

interface QuickStatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subtext?: string;
  color: 'blue' | 'green' | 'amber' | 'purple' | 'red';
  alert?: boolean;
}

function QuickStatCard({ icon, label, value, subtext, color, alert }: QuickStatCardProps) {
  const colorClasses = {
    blue: 'text-blue-400 bg-blue-500/10',
    green: 'text-green-400 bg-green-500/10',
    amber: 'text-amber-400 bg-amber-500/10',
    purple: 'text-purple-400 bg-purple-500/10',
    red: 'text-red-400 bg-red-500/10',
  };

  return (
    <div className={cn(
      'card p-4',
      alert && 'border-amber-500/50 animate-pulse-slow'
    )}>
      <div className="flex items-start gap-3">
        <div className={cn('p-3 rounded-xl', colorClasses[color])}>
          {icon}
        </div>
        <div className="flex-1">
          <p className="text-2xl font-bold text-white">{value}</p>
          <p className="text-sm text-slate-400">{label}</p>
          {subtext && <p className="text-xs text-slate-500 mt-1">{subtext}</p>}
        </div>
      </div>
    </div>
  );
}

interface LeadListItemProps {
  lead: Lead;
  onClick: () => void;
  showHotStatus?: boolean;
}

function LeadListItem({ lead, onClick, showHotStatus }: LeadListItemProps) {
  const name = lead.contact ? `${lead.contact.name_first} ${lead.contact.name_last}`.trim() || 'بدون اسم' : 'بدون اسم';
  
  return (
    <div
      onClick={onClick}
      className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-700/50 cursor-pointer transition-colors"
    >
      <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0">
        <Users className="w-5 h-5 text-slate-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium truncate">{name}</p>
        <p className="text-sm text-slate-400 truncate">{lead.product?.title || 'بدون منتج'}</p>
      </div>
      <div className="flex flex-col items-end gap-1">
        <span className="text-xs text-slate-500">{formatRelativeTime(lead.created_at)}</span>
        {showHotStatus && !lead.entered_in_hot && (
          <span className="text-xs text-amber-400">لم يدخل HOT</span>
        )}
      </div>
    </div>
  );
}

interface OrderCardProps {
  lead: Lead;
  onClick: () => void;
  completed?: boolean;
}

function OrderCard({ lead, onClick, completed }: OrderCardProps) {
  const name = lead.contact ? `${lead.contact.name_first} ${lead.contact.name_last}`.trim() || 'بدون اسم' : 'بدون اسم';
  
  return (
    <div
      onClick={onClick}
      className={cn(
        'p-4 rounded-lg cursor-pointer transition-colors mb-2',
        completed ? 'bg-green-500/5 hover:bg-green-500/10' : 'bg-amber-500/5 hover:bg-amber-500/10'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium truncate">{name}</p>
          <p className="text-sm text-slate-400 truncate mt-1">{lead.product?.title || 'بدون منتج'}</p>
          <div className="flex items-center gap-2 mt-2">
            <ProjectBadge projectId={lead.project_id} />
            <span className="text-xs text-slate-500">{formatRelativeTime(lead.created_at)}</span>
          </div>
        </div>
        <div className="text-left">
          {lead.product?.price_cash && (
            <p className="text-lg font-bold text-primary-400">
              {formatCurrency(lead.product.price_cash)}
            </p>
          )}
          {completed ? (
            <span className="flex items-center gap-1 text-xs text-green-400 mt-1">
              <CheckCircle className="w-3 h-3" />
              تم
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-amber-400 mt-1">
              <Clock className="w-3 h-3" />
              معلق
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
